import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, MessageCircleQuestion, MessagesSquare, Smile } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { Paso1State } from '../../../hooks/api/baja';

const REASONS = [
  { value: 'tiempo', label: 'Falta de tiempo o carga personal' },
  { value: 'otra-oportunidad', label: 'Encontré otra oportunidad laboral' },
  { value: 'modelo', label: 'Dificultades con el modelo o la plataforma' },
  { value: 'personales', label: 'Motivos personales' },
  { value: 'honorarios', label: 'Honorarios / razones económicas' },
  { value: 'otro', label: 'Otro' },
] as const;

const NPS_OPTIONS = [
  { value: 1, emoji: '😞', label: 'Para nada' },
  { value: 2, emoji: '😕', label: 'Poco probable' },
  { value: 3, emoji: '😐', label: 'Tal vez' },
  { value: 4, emoji: '🙂', label: 'Probable' },
  { value: 5, emoji: '😄', label: 'Sin dudas' },
] as const;

/** Encabezado de sección — mismo patrón de ícono en badge + título en negrita
 * que se usa en Paso3PlanTransicion.tsx, para que las 3 tarjetas de este
 * paso se lean con la misma jerarquía que el resto del wizard. */
function SectionHeader({ icon, title, required }: { icon: React.ReactNode; title: string; required?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary shrink-0">
        {icon}
      </span>
      <h2 className="text-base font-bold text-gray-900">
        {title} {required && <span className="text-coral">*</span>}
      </h2>
    </div>
  );
}

interface Props {
  initial: Paso1State | null;
  onBack: () => void;
  onNext: (state: Paso1State) => void;
}

export const Paso1Motivo: React.FC<Props> = ({ initial, onBack, onNext }) => {
  const [reason, setReason] = useState(initial?.reason ?? '');
  const [reasonOther, setReasonOther] = useState(initial?.reasonOther ?? '');
  const [trigger, setTrigger] = useState(initial?.trigger ?? '');
  const [wouldChange, setWouldChange] = useState(initial?.wouldChange ?? '');
  const [recommend, setRecommend] = useState<number | null>(initial?.recommend ?? null);
  const [showErrors, setShowErrors] = useState(false);

  const reasonOtherMissing = reason === 'otro' && !reasonOther.trim();
  const puedeContinuar = !!reason && !reasonOtherMissing && recommend !== null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!puedeContinuar || recommend === null) {
      setShowErrors(true);
      return;
    }
    onNext({ reason, reasonOther: reasonOther || undefined, trigger: trigger || undefined, wouldChange: wouldChange || undefined, recommend });
  };

  return (
    <div className="mx-auto w-full max-w-2xl space-y-8">
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-semibold leading-tight text-gray-900">
          ¿Qué te llevó a tomar esta decisión?
        </h1>
        <p className="text-sm text-muted-foreground">
          Tus respuestas son de uso interno del equipo. Contestar con honestidad nos ayuda a entender mejor
          la experiencia de las profesionales y mejorar las condiciones del equipo.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        <fieldset className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-3">
          <SectionHeader icon={<MessageCircleQuestion className="h-4 w-4" />} title="Motivo principal" required />
          <div className="space-y-2">
            {REASONS.map((r) => (
              <label
                key={r.value}
                className={[
                  'flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors',
                  reason === r.value ? 'border-primary bg-primary/5' : 'border-border bg-white hover:bg-muted',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="reason"
                  className="accent-primary"
                  checked={reason === r.value}
                  onChange={() => { setReason(r.value); setShowErrors(false); }}
                />
                <span className="text-sm text-gray-900">{r.label}</span>
              </label>
            ))}
          </div>
          {showErrors && !reason && (
            <p className="text-xs text-coral">Seleccioná un motivo para continuar.</p>
          )}

          {reason === 'otro' && (
            <div className="pt-2 space-y-2">
              <label className="text-sm text-gray-700">
                Contanos brevemente <span className="text-coral">*</span>
              </label>
              <input
                value={reasonOther}
                maxLength={300}
                onChange={(e) => setReasonOther(e.target.value)}
                placeholder="Tu motivo en pocas palabras"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              />
              {showErrors && reasonOtherMissing && (
                <p className="text-xs text-coral">Contanos brevemente para continuar.</p>
              )}
            </div>
          )}
        </fieldset>

        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-5">
          <SectionHeader icon={<MessagesSquare className="h-4 w-4" />} title="Un poco más de contexto" />
          <p className="text-xs text-muted-foreground -mt-3">Opcional — cuanto más contexto nos des, mejor podemos mejorar como equipo.</p>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">¿Hubo algo puntual que aceleró esta decisión?</label>
            <Textarea
              value={trigger}
              maxLength={1000}
              onChange={(e) => setTrigger(e.target.value)}
              placeholder="Podés ser tan específica como quieras. Esta información es solo interna."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">¿Hay algo que hubiera cambiado tu decisión?</label>
            <Textarea
              value={wouldChange}
              maxLength={1000}
              onChange={(e) => setWouldChange(e.target.value)}
              placeholder="Condiciones económicas, modelo de trabajo, acompañamiento del equipo, herramientas disponibles..."
              rows={3}
            />
          </div>
        </div>

        <fieldset className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-4">
          <SectionHeader icon={<Smile className="h-4 w-4" />} title="¿Recomendarías Psi Mammoliti a una colega?" required />
          <div className="flex flex-wrap gap-2 sm:gap-3">
            {NPS_OPTIONS.map(({ value, emoji, label }) => {
              const selected = recommend === value;
              return (
                <button
                  type="button"
                  key={value}
                  onClick={() => { setRecommend(value); setShowErrors(false); }}
                  className="group flex flex-1 min-w-[64px] flex-col items-center gap-2"
                >
                  <span
                    className={[
                      'flex h-14 w-14 items-center justify-center rounded-full border text-2xl transition-all',
                      selected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30 scale-110'
                        : 'border-border bg-white grayscale opacity-70 group-hover:opacity-100 group-hover:grayscale-0',
                    ].join(' ')}
                    aria-hidden
                  >
                    {emoji}
                  </span>
                  <span className={['text-[11px] leading-tight text-center', selected ? 'text-gray-900 font-semibold' : 'text-gray-500'].join(' ')}>
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          {showErrors && recommend === null && (
            <p className="text-xs text-coral">Seleccioná una opción para continuar.</p>
          )}
        </fieldset>

        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-between pt-4 border-t border-border">
          <Button type="button" variant="ghost" size="default" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Volver
          </Button>
          <Button type="submit" className="sm:min-w-[200px]">
            Continuar <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Paso1Motivo;
